import { CheckCircleFilled, CloseCircleFilled, GoogleOutlined } from "@ant-design/icons";
import { Button, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { loginGoogle } from "../../../../services/auth";
import { actions as AuthActions } from "../../../../store/authRedux/actions";
import { actions as CommonActions } from "../../../../store/commonRedux/actions";
import { requestFcmToken, settleWithin } from "../../../../utils/Messaging";
import useLoginLanding from "../../useLoginLanding";
import styles from "./styles.module.css";

/* همان مهلتی که صفحه‌ی ورود با کد پیامکی به گرفتن توکن پوش می‌دهد: اجازه‌ی
 * نوتیفیکیشن نباید ورود را نگه دارد. */
const FCM_TOKEN_WAIT_MS = 2000;

/* اگر ورود نگرفت، کاربر روی یک صفحه‌ی بن‌بست نمی‌ماند و خودش به فرم ورود
 * برمی‌گردد — با فرصتی برای خواندن دلیل. */
const BOUNCE_BACK_MS = 4000;

const ERROR_MESSAGES = {
  google_auth_failed: "ورود با گوگل انجام نشد. دوباره تلاش کنید.",
  google_not_configured: "ورود با گوگل در حال حاضر فعال نیست.",
};

/**
 * صفحه‌ی بازگشت از گوگل.
 *
 * سرویس، کاربر را با یک `access_token` کوتاه‌عمرِ گوگل به اینجا می‌فرستد و تنها
 * کاری که اینجا انجام می‌شود تبدیل آن به یک نشست تسکامون است. اگر ایمیل به هیچ
 * حسابی نخورَد، سرویس «کاربر یافت نشد» می‌دهد و همان‌جا تمام است: از این مسیر
 * حساب ساخته نمی‌شود.
 */
const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { land } = useLoginLanding();

  const [error, setError] = useState(null);
  const started = useRef(false);

  const goToLogin = () => navigate("/login", { replace: true });

  const googleUser = useMutation(loginGoogle, {
    onSuccess: (res, variables) => {
      const user = res?.data?.user;

      if (variables?.body?.fcm_token) {
        dispatch(AuthActions.setFcmToken(variables.body.fcm_token));
      }

      dispatch(CommonActions.setTempData({ tempData: res.data }));
      dispatch(AuthActions.setAllOrganizations(user?.organizations || []));
      dispatch(AuthActions.setCustomerOf(res.data.customer_of || []));
      dispatch(AuthActions.login(res.data));

      /* نام و نام خانوادگی از گوگل نمی‌آید و اینجا هم پرسیده نمی‌شود: حسابی که
         با گوگل وارد می‌شود از قبل وجود دارد، پس اگر ناقص باشد همان ویزارد
         همیشگی در ادامه‌ی مسیر آن را می‌گیرد. */
      setTimeout(
        () =>
          land({
            organizations: user?.organizations,
            memberships: res.data.customer_of,
          }),
        500,
      );
    },
    /* پیام خطا را همان interceptor نشان می‌دهد؛ اینجا فقط صفحه باید از حالت
       «در حال ورود» بیرون بیاید. */
    onError: (err) =>
      setError(
        err?.response?.data?.message || "ورود با گوگل انجام نشد. دوباره تلاش کنید.",
      ),
  });

  useEffect(() => {
    /* یک بار و فقط یک بار: در حالت StrictMode این افکت دو بار اجرا می‌شود و
       یک access_token دو بار مصرف می‌شود. */
    if (started.current) return;
    started.current = true;

    const failure = searchParams.get("error");
    const accessToken = searchParams.get("access_token");

    if (failure || !accessToken) {
      setError(
        ERROR_MESSAGES[failure] ||
          "ورود با گوگل انجام نشد. دوباره تلاش کنید.",
      );
      return;
    }

    (async () => {
      const fcm_token = await settleWithin(requestFcmToken(), FCM_TOKEN_WAIT_MS);

      googleUser.mutate({
        body: {
          access_token: accessToken,
          ...(fcm_token ? { fcm_token, platform: "web" } : {}),
        },
      });
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!error) return undefined;

    const timer = window.setTimeout(goToLogin, BOUNCE_BACK_MS);
    return () => window.clearTimeout(timer);
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = googleUser.isSuccess && !error;

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div
          className={`${styles.badge} ${
            error ? styles.badgeError : done ? styles.badgeDone : ""
          }`}
        >
          {error ? (
            <CloseCircleFilled />
          ) : done ? (
            <CheckCircleFilled />
          ) : (
            <GoogleOutlined />
          )}
        </div>

        <span className={styles.title}>
          {error ? "ورود ناموفق" : done ? "خوش آمدید" : "در حال ورود با گوگل"}
        </span>

        <p className={styles.subtitle}>
          {error || (done ? "در حال انتقال به پنل..." : "چند لحظه صبر کنید")}
        </p>

        {error ? (
          <Button type="primary" size="large" block className={styles.action} onClick={goToLogin}>
            بازگشت به صفحه ورود
          </Button>
        ) : (
          <Spin />
        )}
      </div>
    </div>
  );
};

export default GoogleCallback;
